"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getJrPlusCollectedTargetCount = getJrPlusCollectedTargetCount;
exports.getJrPlusDestroyedTargetCount = getJrPlusDestroyedTargetCount;
exports.getJrPlusPowerStore = getJrPlusPowerStore;
exports.getMostRecentStudentResponse = getMostRecentStudentResponse;
exports.getNextQuestionForCurrentQuestionPhase = getNextQuestionForCurrentQuestionPhase;
exports.getQuestionPhaseForStudent = getQuestionPhaseForStudent;
exports.getQuestionPhaseQuestionIdsForStudent = getQuestionPhaseQuestionIdsForStudent;
exports.getStudentCorrectQuestionCountForQuestionPhase = getStudentCorrectQuestionCountForQuestionPhase;
exports.getStudentQuestionData = getStudentQuestionData;
exports.isJrPlusCommunicationActive = isJrPlusCommunicationActive;
exports.isJrPlusDefenseActive = isJrPlusDefenseActive;
exports.isJrPlusPowerActive = isJrPlusPowerActive;
exports.isJrPlusRepairsActive = isJrPlusRepairsActive;
exports.isJrPlusThrustersActive = isJrPlusThrustersActive;
exports.isJrPlusTractorBeamActive = isJrPlusTractorBeamActive;
exports.isJrPlusTransporterActive = isJrPlusTransporterActive;
exports.questionPhaseIsComplete = questionPhaseIsComplete;
exports.studentHasAnsweredQuestionCorrectlySince = studentHasAnsweredQuestionCorrectlySince;
exports.studentHasAnsweredQuestionSince = studentHasAnsweredQuestionSince;
exports.studentHasCompletedQuestionPhase = studentHasCompletedQuestionPhase;
var _missionStateConstants = require("../constants/missionStateConstants");
var _general = require("./general");
var _functions = require("./../utility/functions");
var _students = require("./students");
/**
 * Determines if the repairs station for jr plus mission is active or not.
 * @param {?JuniorPlusMissionFullState} jrPlusMissionState
 * @returns {boolean}
 */
function isJrPlusRepairsActive(jrPlusMissionState) {
  return jrPlusMissionState?.stationData.decks.active || false;
}
/**
 * Whether or not the tractor beam station is active on a standard mission
 * @param {?JuniorPlusMissionFullState} missionState
 * @returns {boolean}
 */
function isJrPlusTractorBeamActive(jrPlusMissionState) {
  return !!jrPlusMissionState && jrPlusMissionState.stationData.tractorBeam.status === _missionStateConstants.TRACTOR_BEAM_STATION.STATUS.ACTIVE;
}

/**
 * Whether or not the defense station is active on a standard mission
 * @param {?JuniorPlusMissionFullState} missionState
 * @returns {boolean}
 */
function isJrPlusDefenseActive(jrPlusMissionState) {
  return !!jrPlusMissionState && jrPlusMissionState.stationData.defense.status === _missionStateConstants.DEFENSE_STATION.STATUS.ACTIVE;
}

/**
 * Whether or not the power station is active on a standard mission
 * @param {?JuniorPlusMissionFullState} missionState
 * @returns {boolean}
 */
function isJrPlusPowerActive(jrPlusMissionState) {
  const store = getJrPlusPowerStore(jrPlusMissionState);
  return store && store.active || false;
}

/**
 * Gets the current power station data from the mission
 * @param {?JuniorPlusMissionFullState} missionState
 * @returns {?PowerData}
 */
function getJrPlusPowerStore(jrPlusMissionState) {
  return jrPlusMissionState && jrPlusMissionState.stationData.power;
}

/**
 * Whether or not the communication station is active on a standard mission
 * @param {?JuniorPlusMissionFullState} missionState
 * @returns {boolean}
 */
function isJrPlusCommunicationActive(jrPlusMissionState) {
  return !!jrPlusMissionState && jrPlusMissionState.stationData.communication.active;
}

/**
 * Whether or not the transporter station is active on a standard mission
 * @param {?JuniorPlusMissionFullState} missionState
 * @returns {boolean}
 */
function isJrPlusTransporterActive(jrPlusMissionState) {
  return !!(jrPlusMissionState && jrPlusMissionState.stationData.transporter.status !== 'OFFLINE');
}

/**
 * Whether or not the thrusters station is active on a standard mission
 * @param {?JuniorPlusMissionFullState} missionState
 * @returns {boolean}
 */
function isJrPlusThrustersActive(jrPlusMissionState) {
  return !!(jrPlusMissionState && jrPlusMissionState.stationData.thrusters.isActive);
}

/**
 * Gets an object telling how many times each target on the defense station has been destroyed.
 * @param {JrPlusDefenseData_Active} defense
 * @return {Object} The number of times that targets have been destroyed
 */
function getJrPlusDestroyedTargetCount(defense) {
  const totalDestroyed = {};
  Object.keys(defense.students).forEach(studentId => {
    const {
      destroyed
    } = defense.students[studentId];
    Object.keys(destroyed).forEach(targetId => {
      totalDestroyed[targetId] = (totalDestroyed[targetId] || 0) + destroyed[targetId];
    });
  });
  Object.keys(defense.targetMap).forEach(targetId => {
    if (!totalDestroyed[targetId]) {
      totalDestroyed[targetId] = 0;
    }
  });
  return totalDestroyed;
}

/**
 * Gets an object telling how many times each target on the tractor beam station has been collected.
 * @param {JrPlusTractorBeamData_Active} tractorBeam
 * @return {Object} The number of times that targets have been collected
 */
function getJrPlusCollectedTargetCount(tractorBeam) {
  const totalCollected = {};
  (0, _functions.forEachEntry)(tractorBeam.students, ([, student]) => {
    (0, _functions.forEachEntry)(student.collected, ([targetId, collectedCount]) => {
      totalCollected[targetId] = (totalCollected[targetId] || 0) + collectedCount;
    });
  });
  Object.keys(tractorBeam.targetMap).forEach(targetId => {
    if (!totalCollected[targetId]) {
      totalCollected[targetId] = 0;
    }
  });
  return totalCollected;
}

/**
 * A selector which returns the question phase to consider for a student who could potentially be in training mode.
 * @param {JuniorPlusMissionFullState} state
 * @param {string} studentId
 * @returns QuestionPhase
 */
function getQuestionPhaseForStudent(state, studentId) {
  if ((0, _general.getIsTrainingFromMission)(state) && state.training.status === 'ACTIVE') {
    return state.training.students[studentId]?.questionPhase || {
      status: _missionStateConstants.QUESTION_PHASE.STATUS.INACTIVE
    };
  } else {
    return state.questionPhase;
  }
}

/**
 * Simple selector to get the ordered question ids on the current question phase for a single student.
 * @param {QuestionPhase_Active} questionPhase The active question phase where the data can be found
 * @param {StudentId} studentId The returned question ids will correspond to this student id
 * @return {string[] | void} The list of question ids. If the question phase has not been initialized for this student, returns undefined.
 */
function getQuestionPhaseQuestionIdsForStudent(questionPhase, studentId) {
  return questionPhase.students[studentId]?.questions.ids;
}

/**
 * Gets the next question from the current question phase that should be assigned to the student represented by
 * `studentId`. Returns the first question in the current question phase that was not answered since the current
 * question phase started. If question phase is not active, or the student has already answered all questions in
 * the current question face, returns null.
 * @param {JuniorPlusMissionFullState} state
 * @param {StudentId} studentId
 */
function getNextQuestionForCurrentQuestionPhase(state, studentId) {
  const questionPhase = getQuestionPhaseForStudent(state, studentId);
  if (questionPhase.status !== _missionStateConstants.QUESTION_PHASE.STATUS.ACTIVE) {
    return null;
  }
  const studentQuestions = getQuestionPhaseQuestionIdsForStudent(questionPhase, studentId);
  if (!studentQuestions) {
    return null;
  }
  return studentQuestions.find(questionId => !studentHasAnsweredQuestionSince({
    state,
    studentId,
    questionId,
    since: questionPhase.startTimestamp
  })) || null;
}

// The argument for selectors that query question responses since a given time

/**
 * Get question data for a given student.
 * @param {JuniorPlusMissionFullState} state
 * @param {string} studentId
 * @returns { JuniorPlusStudentQuestionData }
 */
function getStudentQuestionData(state, studentId) {
  if ((0, _general.getIsTrainingFromMission)(state) && state.training.status === 'ACTIVE') {
    return state.training.students[studentId].questionData;
  }
  return state.questionData[studentId];
}

/**
 * Tells whether the student represented by `studentId` has answered the question `questionId`
 * since the timestamp `since`.
 * @param {SinceSelectorArg} args
 * @param {JuniorPlusMissionFullState} args.state
 * @param {string} args.questionId
 * @param {StudentId} args.studentId
 * @param {number} args.since
 * @return {boolean}
 */
function studentHasAnsweredQuestionSince({
  state,
  studentId,
  questionId,
  since: sinceTimestamp
}) {
  let questionData = getStudentQuestionData(state, studentId);
  const questionResponses = questionData?.questionResponses[questionId];
  if (!questionResponses) {
    return false;
  }
  return Object.keys(questionResponses).some(requestId => questionResponses[requestId].responseTimestamp > sinceTimestamp);
}

/**
 * Gets a student's most recent response for the given question.
 * @param {Object} args
 * @param {JuniorPlusMissionFullState} args.state
 * @param {string} args.questionId
 * @param {StudentId} args.studentId
 * @return {?{response: ?JuniorPlusQuestionRequestResponse, requestId: string}}
 */
function getMostRecentStudentResponse({
  state,
  questionId,
  studentId
}) {
  const questionResponses = getStudentQuestionData(state, studentId)?.questionResponses[questionId];
  if (!questionResponses) {
    return undefined;
  }
  const responseIdsOrderedDescending = Object.keys(questionResponses).sort((requestIdA, requestIdB) => questionResponses[requestIdB].responseTimestamp - questionResponses[requestIdA].responseTimestamp);
  if (!responseIdsOrderedDescending.length) {
    return undefined;
  }
  return {
    response: questionResponses[responseIdsOrderedDescending[0]],
    requestId: responseIdsOrderedDescending[0]
  };
}

/**
 * Whether the current active question phase is complete, meaning every connected student has answered all questions.
 * If the question phase is not active, returns undefined.
 * @param {JuniorPlusMissionFullState} state
 */
function questionPhaseIsComplete(state) {
  if (state.questionPhase.status !== _missionStateConstants.QUESTION_PHASE.STATUS.ACTIVE) {
    return undefined;
  }
  const connectedStudents = (0, _students.getConnectedStudents)(state);
  return Object.keys(connectedStudents).every(studentId => studentHasCompletedQuestionPhase(state, studentId));
}

/**
 * Whether the student represented by `studentId` has completed the current question phase, meaning they have answered all
 * questions. If the question phase is not active, returns undefined.
 * @param {JuniorPlusMissionFullState} state
 * @param {StudentId} studentId
 */
function studentHasCompletedQuestionPhase(state, studentId) {
  const questionPhase = getQuestionPhaseForStudent(state, studentId);
  if (questionPhase.status !== _missionStateConstants.QUESTION_PHASE.STATUS.ACTIVE) {
    return undefined;
  }
  return getQuestionPhaseQuestionIdsForStudent(questionPhase, studentId)?.every(questionId => studentHasAnsweredQuestionSince({
    state,
    studentId,
    questionId,
    since: questionPhase.startTimestamp
  })) || false;
}

/**
 * Gets the number of questions in the current question phase that the student answered correctly. If the question phase is not active,
 * returns undefined.
 * @param {JuniorPlusMissionFullState} state The full state
 * @param {StudentId} studentId self explanatory
 * @return {number | void} The number of correctly answered questions. `undefined` if question phase is not active.
 */
function getStudentCorrectQuestionCountForQuestionPhase(state, studentId) {
  const questionPhase = getQuestionPhaseForStudent(state, studentId);
  if (questionPhase.status !== _missionStateConstants.QUESTION_PHASE.STATUS.ACTIVE) {
    return undefined;
  }
  let sum = 0;
  getQuestionPhaseQuestionIdsForStudent(questionPhase, studentId)?.forEach(questionId => {
    if (studentHasAnsweredQuestionCorrectlySince({
      state,
      questionId,
      studentId,
      since: questionPhase.startTimestamp
    })) {
      sum++;
    }
  });
  return sum;
}

/**
 * Whether the student represented by `studentId` has answered the given question correctly since the `since` argument.
 * Returns true if the question response score is `null` or at least as high as the perfect score for the question.
 * @param {SinceSelectorArg} arg
 * @param {JuniorPlusMissionFullState} arg.state
 * @param {string} arg.questionId
 * @param {StudentId} arg.studentId
 * @param {number} arg.since
 * @return {boolean}
 */
function studentHasAnsweredQuestionCorrectlySince({
  state,
  studentId,
  questionId,
  since: sinceTimestamp
}) {
  const questionResponses = getStudentQuestionData(state, studentId)?.questionResponses[questionId];
  if (!questionResponses) {
    return false;
  }
  return Object.keys(questionResponses).some(requestId => {
    const response = questionResponses[requestId];
    return response.responseTimestamp > sinceTimestamp && response.score && (response.score.score === null || response.score.score >= response.score.perfectScore);
  });
}